import { IconButton } from "@fluentui/react";
import { getNextColor, IChartProps, ILineChartDataPoint, ILineChartPoints, IVerticalBarChartDataPoint, VerticalBarChart } from "@fluentui/react-charting";
import React, { useLayoutEffect, useRef, useState } from "react";
import { LineChartComponent } from "../../../ChartComponents/LineChartFluent";
import CustomHorizontalBarChart from "../../../BasicComponents/CustomHorizontalBarChart";
import { IMoMVariance, ISentinelDrilldownResponse } from "../../../interfaces/customer-product-v2.interfaces";
import { CustomShimmer } from "../../../helper/CustomShimmer";
import CustomCard from "../../../SharedComponents/CustomCard";
import FlagIndicatorsRow from "../../../SharedComponents/FlagIndicatorsRow";
import { CUSTOM_CARD } from "../../../SharedComponents/Styles/CustomCard.styles";
import { CUSTOMER_VIEW_STYLES } from "../CustomerView.styles";

interface MainChartData {
    title: string;
    lineChartData: ILineChartPoints[];
    variances?: { name: string; description?: string; variance?: IMoMVariance }[];
    isTCT?: number;
    isCAT?: number;
    hasEngagedAdmin?: number;
    kustoLink?: string;
}

interface SentinelDrilldownChartsProps {
    data: ISentinelDrilldownResponse | null;
    isLoading: boolean;
    granularity: string;
    mainChartData?: MainChartData; // Chart data for Monthly view to render main chart in same row
    kustoLink?: string; // Base Kusto link for drilldown charts
    tableKustoLink?: string; // Specific Kusto link for Tables chart
    regionKustoLink?: string; // Specific Kusto link for Region chart
}

const defaultStyles = CUSTOM_CARD.CUSTOM_CARD_DEFAULT_STYLES;

// Consistent chart dimensions
const CHART_HEIGHT = 300;
const CARD_MIN_HEIGHT = 520; // Consistent card height including header, chart, and footer
const REGIONS_PER_PAGE = 5;

const SentinelDrilldownCharts: React.FC<SentinelDrilldownChartsProps> = ({ data, isLoading, granularity, mainChartData, kustoLink, tableKustoLink, regionKustoLink }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [cardWidth, setCardWidth] = useState<number | undefined>();
    const [regionPage, setRegionPage] = useState(0);

    // Reset region page when data changes
    React.useEffect(() => {
        setRegionPage(0);
    }, [data, granularity]);

    useLayoutEffect(() => {
        const calculateWidth = () => {
            let itemsPerRow = 1;

            // Daily: Always one chart per row (stacked vertically)
            // Monthly: Always 3 charts per row (main chart + region + tables)
            if (granularity === "Monthly") {
                itemsPerRow = 3;
            }

            if (containerRef.current) {
                const availableWidth = containerRef.current.offsetWidth;
                const newCardWidth = Math.floor(availableWidth / itemsPerRow) - 16;
                setCardWidth(newCardWidth);
            }
        };

        calculateWidth();
        window.addEventListener("resize", calculateWidth);
        return () => window.removeEventListener("resize", calculateWidth);
    }, [granularity]);

    const transformToChartData = (items: { name: string; value: number }[]): IChartProps[] => {
        return items.map((item, index) => ({
            chartTitle: item.name,
            chartData: [
                {
                    legend: item.name,
                    horizontalBarChartdata: {
                        x: item.value,
                        y: 0
                    },
                    color: getNextColor(index)
                }
            ]
        }));
    };

    // Transform data for vertical bar chart (Region)
    const transformToVerticalBarData = (items: { name: string; value: number }[]): IVerticalBarChartDataPoint[] => {
        return items.map((item, index) => ({
            x: item.name,
            y: item.value,
            color: getNextColor(index)
        }));
    };

    const regionChartData = data?.byRegion ? transformToVerticalBarData(data.byRegion) : [];
    const tableChartData = data?.byTable ? transformToChartData(data.byTable) : [];

    // Paginated region data
    const totalRegionPages = Math.ceil(regionChartData.length / REGIONS_PER_PAGE);
    const paginatedRegionData = regionChartData.slice(
        regionPage * REGIONS_PER_PAGE,
        (regionPage + 1) * REGIONS_PER_PAGE
    );

    const granularityLabel = granularity === "Daily" ? "Daily" : "Monthly";

    const cardContainerStyle = (width?: number): React.CSSProperties => ({
        flex: granularity === "Monthly" ? "1 1 0" : `0 1 ${width ?? 0}px`,
        minWidth: granularity === "Monthly" ? 0 : undefined,
        display: "flex",
        flexDirection: "column"
    });

    // Render variance section for main chart
    const renderVariances = () => {
        if (!mainChartData?.variances || mainChartData.variances.length === 0) return null;

        return (
            <div style={{ display: "flex", gap: "16px", paddingLeft: "20px", justifyContent: "flex-start" }}>
                {mainChartData.variances.map((v, idx) => {
                    const diffValue = parseFloat(v.variance?.diffValue || "0");
                    const isPositive = diffValue >= 0;

                    const seriesData = mainChartData.lineChartData[idx]?.data;
                    const averageVariance =
                      seriesData && seriesData.length > 0
                        ? seriesData.reduce((sum, point) => sum + (point.y ?? 0), 0) / seriesData.length
                        : undefined;

                    return (
                      <div key={`var-${idx}`} style={{ flex: 1 }}>
                        <div style={{ color: getNextColor(idx), fontWeight: "bold", fontSize: "12px" }}>
                          {v.name}
                          <span style={{ marginLeft: "4px", fontSize: "10px", color: "#666" }}>
                            {v.description || "(Total data ingested - GB/month)"}
                          </span>
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 600, padding: "5px 0" }}>
                          {v.variance?.nextValue || "N/A"}
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 600, padding: "5px 0" }}>
                          {averageVariance !== undefined && Number.isFinite(averageVariance)
                            ? (averageVariance / 1000).toFixed(2) + "K (Avg)"
                            : "N/A"}
                        </div>
                        {v.variance && (
                          <div style={{ fontSize: "12px", color: isPositive ? "green" : "red" }}>
                            {isPositive ? "▲" : "▼"} {v.variance.diffValue} ({v.variance.diffPerc})
                          </div>
                        )}
                        <div style={{ fontSize: "11px", color: "#666" }}>
                          v/s previous month ({v.variance?.prevValue || "N/A"})
                        </div>
                      </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div ref={containerRef} style={{
            display: "flex",
            flexWrap: granularity === "Monthly" ? "nowrap" : "wrap",
            gap: "16px",
            alignItems: "stretch",
            marginTop: granularity === "Daily" ? "24px" : undefined
        }}>
            {/* Main Chart Card (for Monthly view) */}
            {mainChartData && (
                <div style={cardContainerStyle(cardWidth)}>
                    <CustomCard
                        cardHeaderTitle={mainChartData.title}
                        kustoLink={mainChartData.kustoLink}
                        styles={{ root: { minHeight: CARD_MIN_HEIGHT, height: "100%" } }}
                        flagIndicatorComponent={
                            <div className={defaultStyles.cardFooterOuter}>
                                <FlagIndicatorsRow
                                    isTCT={mainChartData.isTCT}
                                    isCAT={mainChartData.isCAT}
                                    hasEngagedAdmin={mainChartData.hasEngagedAdmin}
                                />
                            </div>
                        }
                    >
                        {renderVariances()}
                        <div style={{ backgroundColor: "white", height: CHART_HEIGHT, display: "flex", flexDirection: "column" }}>
                            <LineChartComponent
                                data={{ lineChartData: mainChartData.lineChartData }}
                                height={CHART_HEIGHT}
                                width={cardWidth}
                                allowResize={true}
                                selectedFamily="Microsoft Sentinel"
                            />
                        </div>
                    </CustomCard>
                </div>
            )}
            {/* Table Chart Card */}
            <div style={cardContainerStyle(cardWidth)}>
                <CustomCard
                    cardHeaderTitle={`Top 10 Tables - ${granularityLabel} Average Ingestion (Last 6 Months)`}
                    kustoLink={tableKustoLink || kustoLink}
                    styles={{ root: { minHeight: CARD_MIN_HEIGHT, height: "100%" } }}
                >
                    <div style={{ backgroundColor: "white", padding: "8px", height: CHART_HEIGHT + 50 }}>
                        {isLoading ? (
                            <CustomShimmer columnCount={3} rowCount={6} styles={{ root: { height: CHART_HEIGHT } }} />
                        ) : tableChartData.length > 0 ? (
                            <CustomHorizontalBarChart
                                data={tableChartData}
                                minHeight={CHART_HEIGHT + 40}
                                itemsPerPage={10}
                                barHeight={10}
                            />
                        ) : (
                            <div style={{ color: "#605e5c", fontSize: "13px", padding: "20px 0", textAlign: "center" }}>
                                No table data available
                            </div>
                        )}
                    </div>
                </CustomCard>
            </div>

            {/* Region Chart Card - Vertical Bar Chart (Last) */}
            <div style={cardContainerStyle(cardWidth)}>
                <CustomCard
                    cardHeaderTitle={`Average ${granularityLabel} Ingestion by Region (Last 6 Months) `}
                    kustoLink={regionKustoLink || kustoLink}
                    styles={{ root: { minHeight: CARD_MIN_HEIGHT, height: "100%" } }}
                >
                    <div style={{ backgroundColor: "white", padding: "8px 0", flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        {isLoading ? (
                            <CustomShimmer columnCount={3} rowCount={6} styles={{ root: { height: CHART_HEIGHT } }} />
                        ) : paginatedRegionData.length > 0 ? (
                            <>
                                <div style={{ flexGrow: 1 }}>
                                    <VerticalBarChart
                                        chartTitle="Ingestion by Region (GB)"
                                        data={paginatedRegionData}
                                        barWidth={50}
                                        yAxisTickCount={5}
                                        hideLegend={true}
                                        enableReflow={true}
                                        strokeWidth={3}
                                        height={CHART_HEIGHT - 20}
                                        styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.verticalBarChart}
                                        width={cardWidth ? cardWidth - 32 : undefined}
                                        maxBarWidth={60}
                                    />
                                </div>
                                {totalRegionPages > 1 && (
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <IconButton
                                            iconProps={{ iconName: "ChevronLeft" }}
                                            onClick={() => setRegionPage(Math.max(0, regionPage - 1))}
                                            disabled={regionPage === 0}
                                        />
                                        <IconButton
                                            iconProps={{ iconName: "ChevronRight" }}
                                            onClick={() => setRegionPage(Math.min(totalRegionPages - 1, regionPage + 1))}
                                            disabled={regionPage === totalRegionPages - 1}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{ color: "#605e5c", fontSize: "13px", padding: "20px 0", textAlign: "center" }}>
                                No region data available
                            </div>
                        )}
                    </div>
                </CustomCard>
            </div>
        </div>
    );
};

export default SentinelDrilldownCharts;
